import type { ProgressState } from '../../domain/progress'
import { contentManifest } from '../../content/manifest'
import type { LessonSummary } from '../../content/manifest'

/**
 * Derives the shell's learning-path view from the content manifest, overlaid
 * with live progress from the repository. This is the single place that decides
 * lesson ordering, status, and which lessons are startable, so Today, Journey,
 * and Library stay consistent.
 *
 * A lesson is *published* when it has a content file; unpublished lessons appear
 * in the path as upcoming previews and are not startable.
 */

export type LessonViewStatus = 'completed' | 'in-progress' | 'available' | 'upcoming'

export interface LessonView {
  id: string
  title: string
  summary: string
  estimatedMinutes: number
  /** 1-based position in the path. */
  order: number
  status: LessonViewStatus
  /** Whether the lesson has content and can be started. */
  published: boolean
}

export interface LearningPathView {
  name: string
  lessons: LessonView[]
  total: number
  completedCount: number
  /** Fraction complete in [0, 1]. */
  completedFraction: number
  /** The lesson to resume or start next, if the path is not finished. */
  current: LessonView | null
  /** Action label for the current lesson: "Resume" or "Start". */
  currentActionLabel: 'Resume' | 'Start'
}

export function buildLearningPathView(progress: ProgressState): LearningPathView {
  const lessons = contentManifest.lessons
  const statusOf = (id: string) => progress.lessons[id]?.status
  const isPublished = (lesson: LessonSummary) => typeof lesson.file === 'string'

  // The active lesson is the learner's explicit resume target if it is still
  // open and published, otherwise the first published, not-completed lesson.
  const explicit = progress.currentLessonId
  const explicitIndex =
    explicit && statusOf(explicit) !== 'completed'
      ? lessons.findIndex((lesson) => lesson.id === explicit && isPublished(lesson))
      : -1
  const firstOpenIndex = lessons.findIndex(
    (lesson) => isPublished(lesson) && statusOf(lesson.id) !== 'completed',
  )
  const activeIndex = explicitIndex >= 0 ? explicitIndex : firstOpenIndex

  const views: LessonView[] = lessons.map((lesson, index) => ({
    id: lesson.id,
    title: lesson.title,
    summary: lesson.summary,
    estimatedMinutes: lesson.estimatedMinutes,
    order: index + 1,
    published: isPublished(lesson),
    status: deriveStatus(isPublished(lesson), statusOf(lesson.id), index, activeIndex),
  }))

  const completedCount = views.filter((lesson) => lesson.status === 'completed').length
  const current = activeIndex >= 0 ? views[activeIndex] : null

  return {
    name: contentManifest.path,
    lessons: views,
    total: views.length,
    completedCount,
    completedFraction: views.length === 0 ? 0 : completedCount / views.length,
    current,
    currentActionLabel: current?.status === 'in-progress' ? 'Resume' : 'Start',
  }
}

function deriveStatus(
  published: boolean,
  stored: string | undefined,
  index: number,
  activeIndex: number,
): LessonViewStatus {
  if (stored === 'completed') return 'completed'
  // Unpublished lessons are the only ones shown as upcoming and not startable.
  if (!published) return 'upcoming'
  // The active lesson resumes as in-progress; every other published,
  // not-completed lesson is startable regardless of its position, so a learner
  // can begin any published lesson without finishing the earlier ones first.
  if (index === activeIndex && stored === 'in-progress') return 'in-progress'
  return 'available'
}
